import * as React from 'react';

import { Align } from '@base-ui/react/utils/useAnchorPositioning';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getDateLocale } from '@crm/shared/lib/date';
import {
    Field,
    FieldError,
    FieldLabel,
    InputError,
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from '@crm/shared/ui';

import { DEFAULT_END_MONTH, DEFAULT_START_MONTH } from './model/consts';
import { useDatePicker } from './model/useDatePicker';
import { Calendar } from '../Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

import type { Locale } from 'react-day-picker';

type DatePickerProps = Omit<
    React.ComponentProps<'input'>,
    'value' | 'onChange' | 'defaultValue' | 'type'
> & {
    align?: Align;
    value?: string;
    label?: string;
    error?: string;
    errorDisplay?: 'message' | 'tooltip';
    forceErrorTooltipVisible?: boolean;
    invalid?: boolean;
    locale?: Locale;
    startMonth?: Date;
    endMonth?: Date;
    parseDate?: (value: string) => Date | undefined;
    formatDate?: (date: Date | undefined) => string;
    normalizeOnBlur?: boolean;
    fieldClassName?: string;
    labelClassName?: string;
    inputGroupClassName?: string;
    hideLabel?: boolean;
    trigger?: React.ReactElement;
    onChange?: (value: string) => void;
};

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
    (
        {
            align = 'end',
            id = 'date-picker',
            name,
            value = '',
            label,
            placeholder,
            error,
            errorDisplay = 'message',
            forceErrorTooltipVisible = false,
            invalid = Boolean(error),
            disabled,
            locale = getDateLocale(),
            startMonth = DEFAULT_START_MONTH,
            endMonth = DEFAULT_END_MONTH,
            parseDate,
            formatDate,
            normalizeOnBlur = true,
            fieldClassName,
            labelClassName,
            inputGroupClassName,
            hideLabel = false,
            trigger,
            onChange,
            onBlur,
            onKeyDown,
            ...props
        },
        ref
    ) => {
        const { t } = useTranslation();
        const resolvedLabel = label ?? t('crm.common.datePicker.label');
        const resolvedPlaceholder = placeholder ?? t('crm.common.datePicker.placeholder');
        const inputGroupRef = React.useRef<HTMLDivElement>(null);
        const {
            month,
            open,
            selectedDate,
            setMonth,
            setOpen,
            handleNormalizeInputOnBlur,
            handleInputChange,
            handleInputKeyDown,
            handleSelectDate,
        } = useDatePicker({
            value,
            disabled,
            startMonth,
            endMonth,
            parseDate,
            formatDate,
            normalizeOnBlur,
            onChange,
            onKeyDown,
        });
        const handleDatePickerBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
            handleNormalizeInputOnBlur(event);

            const nextFocusedElement = event.relatedTarget;
            const isFocusInsideDatePicker =
                nextFocusedElement instanceof Node &&
                inputGroupRef.current?.contains(nextFocusedElement);

            if (!isFocusInsideDatePicker) {
                onBlur?.(event);
            }
        };
        const errorId = invalid ? `${id}-error` : undefined;
        const calendar = (
            <PopoverContent
                className="w-auto overflow-hidden p-0"
                align={align}
                alignOffset={-8}
                sideOffset={10}
            >
                <Calendar
                    mode="single"
                    required
                    captionLayout="dropdown"
                    selected={selectedDate}
                    month={month}
                    locale={locale}
                    startMonth={startMonth}
                    endMonth={endMonth}
                    onMonthChange={setMonth}
                    onSelect={handleSelectDate}
                />
            </PopoverContent>
        );

        if (trigger) {
            return (
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger disabled={disabled} render={trigger} />
                    {calendar}
                </Popover>
            );
        }

        return (
            <Field className={fieldClassName ?? 'mx-auto w-full'} data-invalid={invalid}>
                {!hideLabel && (
                    <FieldLabel htmlFor={id} className={labelClassName}>
                        {resolvedLabel}
                    </FieldLabel>
                )}
                <InputGroup ref={inputGroupRef} className={inputGroupClassName}>
                    <InputGroupInput
                        id={id}
                        ref={ref}
                        name={name}
                        value={value}
                        {...props}
                        placeholder={resolvedPlaceholder}
                        inputMode="numeric"
                        aria-invalid={invalid}
                        aria-describedby={errorId}
                        disabled={disabled}
                        onChange={handleInputChange}
                        onBlur={handleDatePickerBlur}
                        onKeyDown={handleInputKeyDown}
                    />
                    {invalid && errorDisplay === 'tooltip' && (
                        <InputError
                            tooltipId={errorId}
                            errorMessage={error}
                            iconClassName="size-3.5"
                            forceTooltipVisible={forceErrorTooltipVisible}
                        />
                    )}
                    <InputGroupAddon align="inline-end">
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger
                                render={
                                    <InputGroupButton
                                        id={`${id}-trigger`}
                                        variant="ghost"
                                        size="icon-xs"
                                        aria-label={t('crm.common.datePicker.openAriaLabel')}
                                        disabled={disabled}
                                    >
                                        <CalendarIcon />
                                        <span className="sr-only">
                                            {t('crm.common.datePicker.openAriaLabel')}
                                        </span>
                                    </InputGroupButton>
                                }
                            />
                            {calendar}
                        </Popover>
                    </InputGroupAddon>
                </InputGroup>
                {errorDisplay === 'message' && <FieldError id={errorId}>{error}</FieldError>}
            </Field>
        );
    }
);

DatePicker.displayName = 'DatePicker';
